/**
 * viewportOverrides — XDATA round-trip for per-VP layer state.
 *
 * AutoCAD stores per-viewport frozen layers in the viewport entity XDATA
 * under app ID "ACAD_LAYERSTATES" or as part of DXF group codes on the VP
 * entity itself (group 68 = on/off status bitmask; actual per-VP freeze
 * lives in group codes inside the VP entity record and optionally in
 * XDATA). For our round-trip we use a dedicated app ID "EDIT_DWG_VP_OVERRIDES"
 * that stores:
 *   1001  EDIT_DWG_VP_OVERRIDES
 *   1002  {
 *   1000  frozenLayer1  (0..N)
 *   1000  frozenLayer2
 *   ...
 *   1040  scaleDenom  (real/double, e.g. 100.0 for 1:100)  [P2 fix: was 1070]
 *   1040  scaleNum    (real/double, e.g. 1.0 for 1:100)    [P2 fix: was 1071]
 *   1070  lockedFlag  (0 or 1)
 *   1071  writerVersion (=VP_OVERRIDES_WRITER_VERSION; #304 r2 — MUST follow
 *         the 1040 scale codes; absent in every pre-marker file)
 *   1000  ucsName     (optional; omit if empty)
 *   1002  }
 *
 * LEGACY FORMAT (reader accepts both for backward compat):
 *   1070  scaleDenom  (was integer — truncates decimals)
 *   1071  scaleNum    (was integer — truncates decimals)
 *   (legacy blocks never carry a writerVersion marker: a 1071 is only read as
 *   the marker when 1040 scale codes precede it)
 *
 * IMPORTANT: unknown XDATA app-id groups are preserved (round-trip fidelity).
 * The frozenLayers array is also read from/written to the ViewportEntity.frozenLayers
 * field directly for in-memory state — XDATA is only for DXF file round-trip.
 */

export const VP_OVERRIDES_APPID = "EDIT_DWG_VP_OVERRIDES";

/**
 * Writer-version marker carried inside OUR XDATA block as a `1071 <version>`
 * group right after the lock flag (#304 r2). MONOTONIC — each version subsumes
 * the guarantees of every lower one.
 *
 * Version 2 = the post-truth-correction writer (S-VIEWPORT-A): it emits
 * VIEWPORT group 142 ONLY as view contrast, never as the annotation-scale
 * factor. Files from the legacy PR-13 window (252db54b → S-VIEWPORT-A) NEVER
 * carry this marker, so the legacy 142-recovery in dxfBridge.drawingToDxf.ts
 * fires only when the marker is ABSENT — a fresh export whose genuine
 * contrast happens to equal the XDATA scale ratio is never mis-recovered.
 *
 * Version 3 = the EXPORT-VP-FROZEN consistent-native-331 writer (#486): it keeps
 * the native group-331 frozen-layer handles CONSISTENT with the XDATA frozen-name
 * list on every export (`effectiveFrozenNames` unions the resolvable 331 names
 * into the XDATA carrier; a VPLAYER thaw POSITIVELY drops the layer's 331 handle),
 * so a v3+ file never carries a stale post-thaw 331. That guarantee is what lets
 * IMPORT trust a v3+ file's native 331 as ADD-authoritative — a resolvable handle
 * absent from the (otherwise consistent) XDATA names can only have been added by an
 * external native writer after our export, so it is a genuine per-VP freeze to
 * PRESERVE, not a legacy stale-thaw artifact to prune (see
 * `VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION` and `collectXdataAuthoritativeVpHandles`).
 *
 * Wire-compat: a 1071 inside a 1040-format block is a no-op for pre-marker
 * readers (their 1071 branch is legacy-scaleNum-only, gated on the ABSENCE of
 * 1040 codes), so older builds read v2/v3 files unchanged. The marker MUST be
 * emitted after the 1040 scale codes for the same reason. The 142-recovery gates
 * on the marker being ABSENT (`writerVersion === undefined`), so bumping 2→3 is
 * transparent to it (both are defined → recovery still never fires on our files).
 */
export const VP_OVERRIDES_WRITER_VERSION = 3;

/**
 * The FIRST writer version whose exporter keeps the native group-331 frozen-layer
 * carrier CONSISTENT with the XDATA frozen-name list (EXPORT-VP-FROZEN, #486). A
 * file whose EDIT_DWG_VP_OVERRIDES block carries a `writerVersion >= this` marker
 * is trusted to have a non-stale native 331, so IMPORT MERGES its native handles
 * (an unlisted resolvable handle = an external post-export ADD to preserve) rather
 * than pruning them against the XDATA names.
 *
 * A FIXED milestone — NOT `VP_OVERRIDES_WRITER_VERSION` — so a future version bump
 * (v4+) does not retroactively distrust genuine v3 files. Pre-marker files and v2
 * files stay on the conservative PRUNE path: a v2 file may predate #486 and carry a
 * stale post-thaw 331 (the old "carry 331 verbatim" behaviour), and v2 alone cannot
 * distinguish a consistent post-#486 export from a stale pre-#486 one — so trusting
 * v2's native 331 could re-freeze a thawed layer (@codex #486 P2 "Honor XDATA
 * freezes before merging native handles"). v3 is the clean, unambiguous line.
 */
export const VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION = 3;

export interface ViewportOverrides {
  frozenLayers: string[];
  scale: { num: number; denom: number };
  locked: boolean;
  ucsName?: string;
}

/**
 * Result of readViewportOverrides — ViewportOverrides plus read-side evidence.
 *
 * `scaleFromXdata` is the rational scale EXACTLY as carried by the
 * EDIT_DWG_VP_OVERRIDES XDATA block: set ONLY when scale group codes were
 * actually present inside OUR appid block (new `1040 denom / 1040 num` format
 * or legacy `1070 denom / 1071 num`). Undefined when the block is absent or
 * carries no scale codes. Unlike `scale` — which falls back to the
 * viewHeight/height derivation and is overridden by the persisted
 * `vpOverrideScale` entity field — this field is pure on-file XDATA evidence.
 * The S-VIEWPORT-A legacy PR-13 142-recovery fingerprint keys off it
 * (dxfBridge.drawingToDxf.ts): a derived/inferred ratio must never be able to
 * trigger that recovery (preserve-source-never-infer).
 *
 * `writerVersion` is the on-file `1071 vpOverridesWriterVersion` marker
 * (#304 r2): set ONLY when OUR block carries the marker group after its 1040
 * scale codes; undefined for every pre-marker file — including the entire
 * legacy PR-13 window. The 142-recovery additionally requires it ABSENT, so
 * v2+ exports can never lose a genuine contrast to a ratio coincidence.
 */
export interface ViewportOverridesReadResult extends ViewportOverrides {
  scaleFromXdata?: { num: number; denom: number };
  writerVersion?: number;
  /**
   * Whether the EDIT_DWG_VP_OVERRIDES block actually CARRIED frozen-layer `1000`
   * name groups (before the scale/lock codes) — i.e. the block GOVERNS the
   * frozen-layer set, as opposed to a scale/lock/UCS-only block that leaves the
   * entity/native baseline authoritative. `xdataOwnsFrozenLayerSet` surfaces this
   * as the import authority predicate (@codex #486 P1). Always set; `false` for a
   * missing/entry-less block.
   */
  hasFrozenLayerGroups: boolean;
}

/** Default overrides — used when no XDATA present. */
export function defaultViewportOverrides(): ViewportOverrides {
  return { frozenLayers: [], scale: { num: 1, denom: 1 }, locked: false };
}

/** XdataEntry type matching the rest of drawing-model. */
interface XdataEntry {
  appName: string;
  groups: { code: number; value: string }[];
}

/**
 * Does a viewport's XDATA carry an EDIT_DWG_VP_OVERRIDES block that actually
 * GOVERNS the frozen-layer set — i.e. contains at least one frozen-layer `1000`
 * group BEFORE any scale/lock code (1040/1070/1071) inside the `{ }` block?
 *
 * This is the SINGLE SOURCE OF TRUTH for "the XDATA owns this viewport's freeze
 * set", mirroring EXACTLY the `frozenLayerGroupsFound` gate inside
 * `readViewportOverrides` (which overwrites `frozenLayers` from XDATA ONLY when
 * leading `1000` groups are present; otherwise the entity/native baseline wins).
 *
 * A scale/lock/UCS-ONLY block (no leading `1000` groups) does NOT own the freeze
 * set. Treating it as authoritative anyway makes the import resolver prune every
 * resolvable native group-331 handle against an EMPTY authoritative name list,
 * silently thawing an AutoCAD-authored freeze (native 331 + scale/lock-only
 * XDATA) or an older-exporter freeze (that exporter wrote `frozenLayers: []` yet
 * preserved the native handles) on import/save (@codex #486 P1). Our OWN new
 * exporter never hits this trap: it drops a thawed layer's native handle
 * positively (applyViewportVplayer), so an intentionally-empty freeze set has an
 * EMPTY `frozenLayerHandles` and nothing to prune.
 *
 * NOTE a trailing `1000` AFTER the scale/lock codes is the ucsName slot, NOT a
 * frozen-layer name; the reader's `scaleOrLockSeen` gate (which this delegates to)
 * makes that distinction.
 */
export function xdataOwnsFrozenLayerSet(
  xdata:
    | readonly {
        appName: string;
        groups: readonly { code: number; value: string }[];
      }[]
    | undefined,
): boolean {
  // Delegate to the ONE VP_OVERRIDES block parser: `readViewportOverrides` already
  // classifies the groups (a `1000` before the scale/lock codes = a frozen-layer
  // name; a trailing `1000` = ucsName) and surfaces the verdict as
  // `hasFrozenLayerGroups`. Reusing it keeps a single source of truth for the block
  // layout instead of a second hand-rolled traversal. It only READS `xdata`, so the
  // cast to the mutable param type is safe; a missing block yields `false`.
  return readViewportOverrides({ xdata: xdata as XdataEntry[] | undefined })
    .hasFrozenLayerGroups;
}

/**
 * Derive a rational scale {num, denom} from the viewport's model-space view
 * height and its paper-space rectangle height.
 *
 * Convention: num:denom means "num paper units : denom model units".
 * For 1:100, viewHeight = 100 × height → { num:1, denom:100 }.
 * Falls back to 1:1 when either value is zero/undefined.
 */
export function scaleFromViewHeightRatio(
  viewHeight?: number,
  vpHeight?: number,
): { num: number; denom: number } {
  if (!viewHeight || !vpHeight || vpHeight <= 0) return { num: 1, denom: 1 };
  // Integer approximation with 3-decimal precision (covers mm/inch DXF units).
  const PREC = 1000;
  const n = Math.round(vpHeight * PREC);
  const d = Math.round(viewHeight * PREC);
  if (n <= 0 || d <= 0) return { num: 1, denom: 1 };
  function gcd(a: number, b: number): number {
    while (b > 0) { const t = b; b = a % b; a = t; }
    return a;
  }
  const g = gcd(n, d);
  return { num: n / g, denom: d / g };
}

/**
 * Read ViewportOverrides from a viewport entity's XDATA.
 * Falls back to defaults for missing fields.
 * Reads displayLocked and frozenLayers directly from entity fields too,
 * merging both sources (XDATA takes precedence when present).
 */
export function readViewportOverrides(viewportEntity: {
  displayLocked?: boolean;
  frozenLayers?: string[];
  xdata?: XdataEntry[];
  viewHeight?: number;
  height?: number;
  vpOverrideScale?: { num: number; denom: number };
  vpOverrideUcsName?: string;
}): ViewportOverridesReadResult {
  // Start with entity-field values as baseline.
  // Prefer vpOverrideScale (persisted authoritative field, set on import) over
  // viewHeight/height derivation. Fall back to derivation only when absent.
  // vpOverrideUcsName is also seeded here so early-return paths (no XDATA) still
  // carry the entity field value.
  const result: ViewportOverridesReadResult = {
    frozenLayers: [...(viewportEntity.frozenLayers ?? [])],
    scale: viewportEntity.vpOverrideScale
      ?? scaleFromViewHeightRatio(viewportEntity.viewHeight, viewportEntity.height),
    locked: viewportEntity.displayLocked ?? false,
    // Overwritten below iff the block carries frozen-layer 1000 groups; a
    // missing/entry-less/scale-only block leaves it false (@codex #486 P1).
    hasFrozenLayerGroups: false,
    ...(viewportEntity.vpOverrideUcsName !== undefined
      ? { ucsName: viewportEntity.vpOverrideUcsName }
      : {}),
  };

  const xdata = viewportEntity.xdata;
  if (!xdata || xdata.length === 0) return result;

  const entry = xdata.find((e) => e.appName === VP_OVERRIDES_APPID);
  if (!entry) return result;

  // Parse groups.
  //
  // NEW format (P2 fix — real-valued scale via 1040):
  //   { 1002 "{" } [ 1000 frozenLayer... ] 1040 scaleDenom 1040 scaleNum
  //   1070 lockedFlag [ 1000 ucsName ] { 1002 "}" }
  //
  // LEGACY format (accepted for backward compat with pre-P2 XDATA):
  //   { 1002 "{" } [ 1000 frozenLayer... ] 1070 scaleDenom 1071 scaleNum
  //   1070 lockedFlag [ 1000 ucsName ] { 1002 "}" }
  const frozen: string[] = [];
  // Track whether any 1000 frozen-layer groups were found in the block.
  // Only overwrite result.frozenLayers when at least one layer group is present;
  // otherwise the entity-field baseline is authoritative (prevents a scale/lock-only
  // XDATA block from clobbering entity frozenLayers to empty — iter-5 P2 fix #1).
  let frozenLayerGroupsFound = false;
  let scaleNum = 1;
  let scaleDenom = 1;
  // Track whether scale codes were actually found in the XDATA block.
  // If absent, result.scale (derived from viewHeight/height) is preserved.
  let scaleCodesFound = false;
  let locked = result.locked;
  let ucsName: string | undefined;
  let inBlock = false;
  // Count how many 1040-coded groups (real scale) we've seen inside the block.
  // First 1040 = scaleDenom, second 1040 = scaleNum.
  let code40Count = 0;
  // Count how many 1070-coded groups we've seen inside the block.
  // NEW format: 1070 (only one) = lockedFlag (after the two 1040 scale groups).
  // LEGACY format: first 1070 = scaleDenom, second 1070 = lockedFlag.
  let code70Count = 0;
  // Saves the raw value of the first 1070 encountered (legacy mode).
  // Used for the post-loop lock-only-block disambiguation (iter-5 P2 fix #2).
  let firstCode70Value = "";
  // Whether we've detected the new 1040-based scale format.
  let hasReal1040Scale = false;
  // Whether a 1071 (legacy scaleNum) was found. Used post-loop to detect a
  // lock-only block that has a single 1070 misread as scaleDenom (iter-5 P2 fix #2).
  let foundLegacyScaleNum = false;
  // On-file writer-version marker (1071 after the 1040 scale codes; #304 r2).
  // Stays undefined for pre-marker files — that absence IS the legacy evidence.
  let writerVersion: number | undefined;
  // Count how many 1000-coded groups we've seen inside the block AFTER
  // the scale/lock groups have started (i.e. the trailing ucsName slot).
  // We separate frozen layers (before any scale/lock group) from ucsName (after).
  let scaleOrLockSeen = false;
  let post70Code1000Count = 0;

  for (const g of entry.groups) {
    if (g.code === 1002 && g.value === "{") {
      inBlock = true;
      continue;
    }
    if (g.code === 1002 && g.value === "}") {
      inBlock = false;
      continue;
    }
    if (!inBlock) continue;

    if (g.code === 1000) {
      if (!scaleOrLockSeen) {
        // Before any scale/lock groups → frozen layer name
        frozen.push(g.value);
        frozenLayerGroupsFound = true;
      } else {
        // After scale/lock groups → ucsName slot (only first occurrence used)
        if (post70Code1000Count === 0) {
          ucsName = g.value || undefined;
        }
        post70Code1000Count++;
      }
    } else if (g.code === 1040) {
      // New real-valued scale codes (P2 fix)
      scaleOrLockSeen = true;
      hasReal1040Scale = true;
      if (code40Count === 0) {
        scaleDenom = parseFloat(g.value) || 1;
        scaleCodesFound = true;
      } else if (code40Count === 1) {
        scaleNum = parseFloat(g.value) || 1;
        scaleCodesFound = true;
      }
      code40Count++;
    } else if (g.code === 1070) {
      scaleOrLockSeen = true;
      if (!hasReal1040Scale) {
        // LEGACY format: first 1070 is scaleDenom
        if (code70Count === 0) {
          firstCode70Value = g.value;
          scaleDenom = parseFloat(g.value) || 1;
          scaleCodesFound = true;
        } else if (code70Count === 1) {
          // LEGACY format: second 1070 is lockedFlag
          locked = parseInt(g.value, 10) !== 0;
        }
      } else {
        // NEW format: single 1070 after 1040s = lockedFlag
        locked = parseInt(g.value, 10) !== 0;
      }
      code70Count++;
    } else if (g.code === 1071) {
      if (!hasReal1040Scale) {
        // LEGACY format only: scaleNum as 32-bit integer
        scaleOrLockSeen = true;
        scaleNum = parseFloat(g.value) || 1;
        scaleCodesFound = true;
        foundLegacyScaleNum = true;
      } else {
        // NEW format: a 1071 AFTER the 1040 scale codes is the writer-version
        // marker (#304 r2) — never a scale value (legacy 1071 scaleNum and
        // 1040 codes are mutually exclusive writer outputs).
        const v = parseInt(g.value, 10);
        if (Number.isFinite(v)) writerVersion = v;
      }
    }
  }

  // Post-loop: if in legacy mode (no 1040 groups) exactly one 1070 was found
  // with no accompanying 1071 scaleNum, the single 1070 was misidentified as
  // scaleDenom but is actually a lock-only flag — retroactively reinterpret.
  // This fixes lock-only XDATA blocks where lock is stored as a single 1070 0/1
  // with no scale groups (iter-5 P2 fix #2).
  if (!hasReal1040Scale && code70Count === 1 && !foundLegacyScaleNum) {
    scaleDenom = 1;
    scaleCodesFound = false;
    locked = parseInt(firstCode70Value, 10) !== 0;
  }

  // Only overwrite frozenLayers when the block actually contained layer-name
  // groups; otherwise the entity-field baseline is authoritative. This prevents
  // a scale/lock-only XDATA block from clobbering entity frozenLayers to empty
  // (iter-5 P2 fix #1 — same class as derived-scale-preserve).
  if (frozenLayerGroupsFound) {
    result.frozenLayers = frozen;
  }
  // Surface the block-owns-freeze-set verdict for `xdataOwnsFrozenLayerSet` (the
  // import authority predicate) — the ONE place the block layout is classified.
  result.hasFrozenLayerGroups = frozenLayerGroupsFound;
  // Only overwrite the derived scale when XDATA actually contained scale codes
  // (1070 scaleDenom and/or 1071 scaleNum). If absent, the viewHeight-derived
  // scale from the initial result is preserved (P2 #5 fix).
  if (scaleCodesFound) {
    result.scale = { num: scaleNum, denom: scaleDenom };
    // Surface the raw XDATA-carried scale as explicit evidence (see
    // ViewportOverridesReadResult doc). Deliberately NOT overridden by the
    // vpOverrideScale entity field below — this must stay what the FILE says.
    result.scaleFromXdata = { num: scaleNum, denom: scaleDenom };
  }
  result.locked = locked;
  if (ucsName !== undefined) result.ucsName = ucsName;
  // Surface the writer-version marker as pure on-file evidence (#304 r2) —
  // like scaleFromXdata it must say what the FILE says, nothing inferred.
  if (writerVersion !== undefined) result.writerVersion = writerVersion;

  // P1 #2 / P2 #3: persisted entity fields are authoritative — they were decoded
  // at import time (toDrawingEntity) and survive without floating-point loss.
  // Override any XDATA-derived value with the entity field when present.
  if (viewportEntity.vpOverrideScale !== undefined) {
    result.scale = viewportEntity.vpOverrideScale;
  }
  if (viewportEntity.vpOverrideUcsName !== undefined) {
    result.ucsName = viewportEntity.vpOverrideUcsName;
  }

  return result;
}

/**
 * Write ViewportOverrides into a viewport entity's XDATA.
 * Preserves all foreign XDATA app-id groups (round-trip fidelity).
 * Also updates entity.displayLocked and entity.frozenLayers directly.
 *
 * `options.omitScale` (viewport-set-view UNDO, null branch): emit the entry
 * WITHOUT the `1040` scale codes AND without the trailing `1071` writer-version
 * marker — i.e. restore the pre-mutation "no VP_OVERRIDES scale" state on a
 * viewport whose displayed scale was purely derived (typed `vpOverrideScale`
 * absent). The block still carries frozenLayers / lock / ucsName. The marker
 * MUST be dropped alongside the 1040 codes: a lone `1071` with no preceding
 * `1040` is read as a LEGACY scaleNum (readViewportOverrides §1071 branch), which
 * would resurrect a phantom scale — the exact bug this mode exists to prevent.
 *
 * NOT gated on native-331 completeness (@codex #514 P2 round-trip note): an
 * earlier revision downgraded this marker below the consistent-331 threshold
 * whenever a frozen NAME had no resolvable LAYER handle (to stop a later
 * re-import from misreading its permanent 331 absence as a thaw). @codex
 * caught that this DISABLES trustworthy-native reconciliation for every OTHER
 * (resolvable) name in the SAME viewport — e.g. after exporting
 * `['GhostWithoutHandle', 'Walls']`, a native editor thawing `Walls` (removing
 * its 331) would be ignored on re-import (legacy PRUNE trusts the stale XDATA
 * name instead), and export would wrongly re-add the 331. The protection the
 * downgrade sought is ALREADY provided, more precisely, by
 * `mergeFrozenLayerNamesFromHandles`'s PASS A: a name with NO resolvable
 * handle is NEVER subtracted regardless of trust mode (PRESERVE-SOURCE,
 * NEVER-INFER), so `GhostWithoutHandle` can never be wrongly thawed by the
 * trust-subtract path in the first place. The residual gap (a FOREIGN tool
 * assigns `GhostWithoutHandle` a LAYER handle sometime AFTER this export
 * without also adding a 331 entry) is a narrower, harder-to-fully-close edge
 * case not worth breaking the common case for.
 */
export function writeViewportOverrides(
  overrides: ViewportOverrides,
  viewportEntity: {
    displayLocked?: boolean;
    frozenLayers?: string[];
    xdata?: XdataEntry[];
  },
  options?: { omitScale?: boolean },
): void {
  const omitScale = options?.omitScale ?? false;
  // Update entity fields directly (in-memory state)
  viewportEntity.displayLocked = overrides.locked;
  viewportEntity.frozenLayers = [...overrides.frozenLayers];

  // Build XDATA groups
  const groups: { code: number; value: string }[] = [];
  groups.push({ code: 1002, value: "{" });
  for (const layer of overrides.frozenLayers) {
    groups.push({ code: 1000, value: layer });
  }
  if (!omitScale) {
    // P2 fix: use real-valued group code 1040 (double) for scale numerator/
    // denominator so decimal custom scales (e.g. 1.5:7.5) survive without
    // truncation. Group code 1070 is 16-bit integer and would truncate.
    groups.push({ code: 1040, value: String(overrides.scale.denom) });
    groups.push({ code: 1040, value: String(overrides.scale.num) });
  }
  groups.push({ code: 1070, value: overrides.locked ? "1" : "0" });
  if (!omitScale) {
    // #304 r2: stamp the writer-version marker on EVERY scale-bearing export. Its
    // presence proves the file post-dates the 142 truth correction, so the legacy
    // 142-recovery (dxfBridge.drawingToDxf.ts) can never eat a genuine contrast
    // that coincides with the scale ratio. Must follow the 1040 codes (see
    // VP_OVERRIDES_WRITER_VERSION doc) — and is therefore omitted whenever the
    // 1040 codes are (see omitScale note above).
    groups.push({ code: 1071, value: String(VP_OVERRIDES_WRITER_VERSION) });
  }
  if (overrides.ucsName) {
    groups.push({ code: 1000, value: overrides.ucsName });
  }
  groups.push({ code: 1002, value: "}" });

  const newEntry: XdataEntry = { appName: VP_OVERRIDES_APPID, groups };

  // Preserve foreign XDATA, replace our entry
  const existing = viewportEntity.xdata ?? [];
  const foreign = existing.filter((e) => e.appName !== VP_OVERRIDES_APPID);
  viewportEntity.xdata = [...foreign, newEntry];
}
